"use client";

import { useEffect } from "react";
import { fireClickBeacon } from "@/components/personalization/RecordDealClick";
import {
  persistClickAttribution,
  sanitizeClickPosition,
} from "@/lib/click-attribution-tracker";

/**
 * Feedback-loop PR-B (taak #29, 2026-08-16): closes the frontend half of
 * "URL carries `?src=newsletter&pos=<n>` but nothing reads it" (audit-cluster
 * 3 — `newsletterDealUrl()` on site-backend already appends the pair, PR-A;
 * `track-click.dto.ts` already declares the matching source/position shape;
 * neither was ever wired to the deal-detail landing page).
 *
 * Drop this on a deal-detail landing page (packages/[category]/[dealId],
 * vouchers/[id], rondreizen/[slug] via the shared renderDealDetail) next to
 * the existing `<RecordDealView dealId={...} />`. On mount, when the URL
 * carried `src`+`pos` (a click that arrived from a tracked internal surface
 * — a newsletter, a carousel, another deal's related-items strip, ...):
 *   1. fires the SAME click beacon a real in-app DealCard click fires
 *      (`fireClickBeacon`, RecordDealClick.js — POST /api/v2/deals/:id/track-click),
 *      so a newsletter landing counts toward popularity/conversion the exact
 *      same way an in-app click already does; and
 *   2. persists the click into sessionStorage, SCOPED to this dealId
 *      (`src/lib/click-attribution-tracker.js`), so `ReservationForm` can
 *      echo it back to the backend at booking-flow-start IF AND ONLY IF the
 *      customer ends up booking this SAME deal — never a different,
 *      since-browsed one (see that module's file doc-comment for the full
 *      anti-misattribution reasoning).
 *
 * A page with no `src`/`pos` in the URL (the overwhelming majority of
 * traffic — organic, direct, an in-app click that already fired its own
 * beacon before navigating here) is a no-op: nothing double-fires, nothing
 * is written.
 */
export default function RecordDealClickFromLanding({ dealId, src, pos }) {
  useEffect(() => {
    if (dealId == null || !src) return;
    // Independent review finding (2026-08-16, re-confirmation round,
    // BLOCKING): an empty or whitespace-only `pos` (`?pos=`, `?pos=%20` —
    // both reachable from a malformed/hand-typed URL, and `pos == null` is
    // false for either) used to fall through the OLD guard here and reach
    // `Number(pos)`, which is `0` for both — a REAL position under this
    // design, so this fired a FABRICATED "clicked position 0" beacon AND
    // stored the same fabricated attribution. sanitizeClickPosition is the
    // single shared fix for both sinks below — see its own doc-comment in
    // click-attribution-tracker.js for the full why.
    const posNum = sanitizeClickPosition(pos);
    if (posNum === undefined) return;

    try {
      fireClickBeacon(dealId, src, posNum);
    } catch {
      // Beacon is fire-and-forget elsewhere too (sendJsonBeacon swallows its
      // own errors) — belt-and-braces so a landing-page mount can never throw.
    }
    try {
      persistClickAttribution({ dealId, src, pos: posNum });
    } catch {
      // Storage blocked / quota full — graceful degrade, same as UtmCapture.
    }
  }, [dealId, src, pos]);

  return null;
}
